import './count-controller.scss'
import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  TouchEvent,
  TouchEventHandler,
  MouseEvent,
  MouseEventHandler,
} from 'react'

interface CountControllerProps {
  value: number
  total: number
  onChange: (value: number) => void
}

export default function CountController(props: CountControllerProps) {
  const { value, total, onChange } = props
  const prevValue = useRef(value)
  const min = 0
  const prevTotal = useRef(0)
  const elStyle = useMemo<React.CSSProperties>(() => {
    if (total <= 0)
      return {
        pointerEvents: 'none',
      }

    return {}
  }, [total])
  const [percent, setPercent] = useState(0)
  const thumbStyle = useMemo<React.CSSProperties>(
    () => ({ width: `${percent}%` }),
    [percent]
  )

  const handleMinus = useCallback(() => {
    if (total <= 0) return

    const newValue = Math.max(min, value - 1)
    onChange(newValue)
  }, [value, total])

  const handlePlus = useCallback(() => {
    if (total <= 0) return

    const newValue = Math.min(total, value + 1)
    onChange(newValue)
  }, [value, total])

  const handleMax = useCallback(() => {
    if (total <= 0) return

    onChange(total)
  }, [total])

  const draggingRef = useRef(false)
  const rectRef = useRef<DOMRect | null>(null)
  const slide = useCallback(
    (clientX: number, _target: HTMLDivElement) => {
      const target = _target.parentElement as HTMLDivElement
      if (target == null) return

      const sliderElem = target.querySelector<HTMLElement>(
        '.count-controller-slider'
      )
      if (sliderElem == null) return

      if (rectRef.current == null) {
        rectRef.current = target.getBoundingClientRect()
      }
      const rect = rectRef.current
      const width = target.offsetWidth
      const offsetX = clientX - rect.left
      const sliderWidth = offsetX <= 0 ? 0 : sliderElem.offsetWidth
      let res = Math.floor(((offsetX + sliderWidth) / width) * 100)
      if (res < 0) {
        res = 0
      } else if (res > 100) {
        res = 100
      }
      onChange(Math.floor(total * (res / 100)))
    },
    [total]
  )

  const slide2 = useCallback(
    (offsetX: number, target: HTMLDivElement) => {
      const sliderElem = target.querySelector<HTMLElement>(
        '.count-controller-slider'
      )
      if (sliderElem == null) return

      // console.log('offsetX =>', offsetX)
      const width = target.offsetWidth
      const sliderWidth = offsetX === 0 ? 0 : sliderElem.offsetWidth
      let res = Math.floor(((offsetX + sliderWidth) / width) * 100)
      if (res < 0) {
        res = 0
      } else if (res > 100) {
        res = 100
      }
      const changeValue = Math.floor(total * (res / 100))
      // console.log('changeValue =>', width, sliderWidth, res)
      onChange(changeValue)
    },
    [total]
  )

  const handleSlideFromTouch = useCallback(
    (e: TouchEvent) => {
      const target = e.nativeEvent.target as HTMLDivElement | null
      const touch = e.nativeEvent.touches[0] || e.nativeEvent.changedTouches[0]
      if (target == null || touch == null) return

      slide(touch.clientX, target)
    },
    [slide]
  )

  const handleSlideFromMouse = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.currentTarget as HTMLDivElement
      slide(e.nativeEvent.clientX, target)
    },
    [slide]
  )
  const handlePointerStart: TouchEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault()
      draggingRef.current = true
    },
    []
  )
  const handlePointerEnd: TouchEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault()
      draggingRef.current = false
      handleSlideFromTouch(e)
      rectRef.current = null
    },
    [handleSlideFromTouch]
  )
  const handlePointerMove: TouchEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault()
      if (!draggingRef.current) return

      handleSlideFromTouch(e)
    },
    [handleSlideFromTouch]
  )

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault()
      draggingRef.current = true
    },
    []
  )

  const handleMouseUp: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    e.preventDefault()
    if (draggingRef.current) {
      handleSlideFromMouse(e)
    }

    draggingRef.current = false
    rectRef.current = null
  }, [])
  const handleGlobalMouseUp: Parameters<HTMLElement['addEventListener']>[1] = (
    e
  ) => {
    e.preventDefault()
    draggingRef.current = false
    rectRef.current = null
  }
  useEffect(() => {
    document.body.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.body.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [handleGlobalMouseUp])

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (!draggingRef.current) return

      handleSlideFromMouse(e)
    },
    [handleSlideFromMouse]
  )

  useEffect(() => {
    if (value === 0 && prevTotal.current === 0 && total > 0) {
      prevTotal.current = total
      onChange(total)
    }
  }, [value, total])

  useEffect(() => {
    prevValue.current = value
    setPercent((value / total) * 100)
  }, [value, total])

  return (
    <div className="count-controller" dir="ltr" style={elStyle}>
      <a className="count-controller__minus" onClick={handleMinus}></a>
      <div className="count-controller-progress-hotspot">
        <div
          className="count-controller-progress-hotspot__inner"
          onTouchStart={handlePointerStart}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerEnd}
          onTouchCancel={handlePointerEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        ></div>
        <div className="count-controller-progress">
          <div className="count-controller-progress__thumb" style={thumbStyle}>
            <div className="count-controller-slider" data-percent={percent}>
              <div className="count-controller-tooltip">
                <span className="count-controller-tooltip__cont">
                  {value}/{total}
                </span>
                <i className="count-controller-tooltip__arrow"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <a className="count-controller__plus" onClick={handlePlus}></a>
      <a className="count-controller__max" onClick={handleMax}></a>
    </div>
  )
}
